import { useEffect, useState } from "react";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { useToast } from "../components/Toast.js";
import type { TenantCustomerRoleType, TenantCustomerUserDetail } from "../lib/customers.js";

interface TenantPersonRow {
  account_id: string;
  email: string;
  display_name: string | null;
  status?: string;
}

interface Props {
  tenantCustomerId: string;
  users: TenantCustomerUserDetail[];
  onChanged: () => void;
}

/**
 * ADR-0075 — Mapped users table for a customer.
 *
 * Single-primary radio (server enforces the swap atomically). On primary
 * removal, the server auto-promotes the next-most-recent mapping. The
 * tenant-level fallback ("default coverage") is a separate surface; this
 * component is customer-specific.
 */
export function CustomerUserMappingTable({ tenantCustomerId, users, onChanged }: Props) {
  const toast = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [addUserId, setAddUserId] = useState("");
  const [addRoleId, setAddRoleId] = useState("");
  const [busy, setBusy] = useState(false);
  const [people, setPeople] = useState<TenantPersonRow[]>([]);
  const [roleTypes, setRoleTypes] = useState<TenantCustomerRoleType[]>([]);
  const [addError, setAddError] = useState<string | null>(null);

  useEffect(() => {
    if (!addOpen) return;
    void (async () => {
      try {
        const [pe, rt] = await Promise.all([
          apiFetch<{ members: TenantPersonRow[] }>("/api/tenant/admin/users"),
          apiFetch<{ role_types: TenantCustomerRoleType[] }>("/api/tenant/admin/customer-role-types")
        ]);
        setPeople((pe.members ?? []).filter((m) => m.status === "active" || m.status === undefined));
        setRoleTypes(rt.role_types ?? []);
      } catch {
        // Non-fatal — user picker may end up empty; the panel still works manually.
      }
    })();
  }, [addOpen]);

  async function addMapping() {
    if (!addUserId || !addRoleId) return;
    setBusy(true);
    setAddError(null);
    try {
      await apiFetch(`/api/tenant/admin/customers/${tenantCustomerId}/users`, {
        method: "POST",
        body: { user_id: addUserId, role_type_id: addRoleId }
      });
      setAddOpen(false);
      setAddUserId("");
      setAddRoleId("");
      onChanged();
    } catch (e) {
      setAddError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function setPrimary(mappingId: string) {
    try {
      await apiFetch(
        `/api/tenant/admin/customers/${tenantCustomerId}/users/${mappingId}`,
        { method: "PATCH", body: { is_primary: true } }
      );
      onChanged();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    }
  }

  async function removeMapping(m: TenantCustomerUserDetail) {
    try {
      await apiFetch(
        `/api/tenant/admin/customers/${tenantCustomerId}/users/${m.id}`,
        { method: "DELETE" }
      );
      onChanged();
    } catch (e) {
      const msg = e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e);
      toast.push({ kind: "error", message: msg });
    }
  }

  const mappedAccountIds = new Set(users.map((u) => u.user_id));
  const availablePeople = people.filter((p) => !mappedAccountIds.has(p.account_id));

  return (
    <div className="card">
      <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
        <div style={{ fontWeight: 600 }}>Mapped users</div>
        <div style={{ marginLeft: "auto" }}>
          <button className="btn btnPrimary" onClick={() => setAddOpen((v) => !v)}>
            + Add user
          </button>
        </div>
      </div>

      {addOpen && (
        <div
          className="card"
          style={{ background: "var(--surface-soft)", marginBottom: 10 }}
        >
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr auto auto" }}>
            <select
              className="select"
              value={addUserId}
              onChange={(e) => setAddUserId(e.target.value)}
            >
              <option value="">Select user…</option>
              {availablePeople.map((p) => (
                <option key={p.account_id} value={p.account_id}>
                  {p.display_name ?? p.email}
                </option>
              ))}
            </select>
            <select
              className="select"
              value={addRoleId}
              onChange={(e) => setAddRoleId(e.target.value)}
            >
              <option value="">Select role…</option>
              {roleTypes
                .filter((r) => !r.is_disabled)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.label} ({r.slug})
                  </option>
                ))}
            </select>
            <button
              className="btn btnPrimary"
              disabled={busy || !addUserId || !addRoleId}
              onClick={() => void addMapping()}
            >
              Add
            </button>
            <button className="btn" onClick={() => setAddOpen(false)} disabled={busy}>
              Cancel
            </button>
          </div>
          {addError && (
            <div className="help" style={{ color: "var(--danger-text)", marginTop: 8 }}>
              {addError}
            </div>
          )}
          {roleTypes.length === 0 && (
            <div className="help muted" style={{ marginTop: 8 }}>
              No role types configured. Use <strong>Manage roles</strong> on the Customers list to add
              one (e.g. AE, CSM).
            </div>
          )}
        </div>
      )}

      {users.length === 0 ? (
        <div className="muted" style={{ padding: 12 }}>
          No users mapped yet.
        </div>
      ) : (
        <div className="tableWrap">
          <table className="table">
            <thead>
              <tr>
                <th>User</th>
                <th>Role</th>
                <th style={{ textAlign: "center", width: 80 }}>Primary</th>
                <th style={{ width: 100 }}></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>
                    {u.display_name ?? u.email}
                    <span className="muted" style={{ marginLeft: 8 }}>
                      {u.email}
                    </span>
                  </td>
                  <td>{u.role_label}</td>
                  <td style={{ textAlign: "center" }}>
                    <input
                      type="radio"
                      name={`primary-${tenantCustomerId}`}
                      checked={u.is_primary}
                      onChange={() => !u.is_primary && void setPrimary(u.id)}
                    />
                  </td>
                  <td>
                    <button
                      className="adminListAction isDanger"
                      onClick={() => void removeMapping(u)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
